import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  CircularProgress,
  Typography,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import { Search, PersonAdd, Check, HourglassEmpty } from '@mui/icons-material';

const FriendSearch = ({ onSearch, onSendRequest, searchResults, loading, sentRequests = new Set() }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [localSentRequests, setLocalSentRequests] = useState(new Set());
  
  // Debounced search function
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length > 2) {
        onSearch(searchQuery.trim());
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearch]);

  const handleSendRequest = async (userId) => {
    try {
      await onSendRequest(userId);
      setLocalSentRequests(prev => new Set([...prev, userId]));
    } catch (error) {
      console.error('Failed to send friend request:', error);
    }
  };

  const isRequestSent = (userId) => {
    return sentRequests.has(userId) || localSentRequests.has(userId);
  };

  const getButtonContent = (user) => {
    if (user.isFriend) {
      return {
        icon: <Check />,
        text: 'Friends',
        disabled: true,
        color: 'success',
        variant: 'outlined'
      };
    }
    
    if (user.hasPendingRequest || isRequestSent(user.id)) {
      return {
        icon: <HourglassEmpty />,
        text: 'Pending',
        disabled: true,
        color: 'warning',
        variant: 'outlined'
      };
    }
    
    return {
      icon: <PersonAdd />,
      text: 'Add Friend',
      disabled: false,
      color: 'primary',
      variant: 'contained'
    };
  };

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search for friends by name or email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
        variant="outlined"
      />

      {searchQuery.length > 0 && searchQuery.length <= 2 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please enter at least 3 characters to search
        </Alert>
      )}

      {searchResults && searchResults.length > 0 ? (
        <List disablePadding>
          {searchResults.map((user) => {
            const buttonProps = getButtonContent(user);
            
            return (
              <ListItem key={user.id} divider>
                <ListItemAvatar>
                  <Avatar 
                    src={user.avatar || user.profilePicture}
                    sx={{ bgcolor: 'primary.main' }}
                  >
                    {(user.name || user.username || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight="medium">
                      {user.name || user.username}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      {user.email && (
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} mt={0.5}>
                        {user.mutualFriends > 0 && (
                          <Chip 
                            label={`${user.mutualFriends} mutual friend${user.mutualFriends !== 1 ? 's' : ''}`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        {user.location && (
                          <Chip 
                            label={user.location} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    variant={buttonProps.variant}
                    color={buttonProps.color}
                    startIcon={buttonProps.icon}
                    onClick={() => !buttonProps.disabled && handleSendRequest(user.id)}
                    disabled={buttonProps.disabled}
                  >
                    {buttonProps.text}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      ) : searchQuery.length > 2 && !loading ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No users found for "{searchQuery}"
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try searching with a different name or email
          </Typography>
        </Box>
      ) : null}

      {searchQuery.length === 0 && (
        <Box textAlign="center" py={4}>
          <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Start typing to search for friends
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FriendSearch;